"use client";

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import {
  designerLinks,
  eventLinks,
  type NavLink,
} from "@/components/layout/navLinks";
import { formats } from "@/components/events/eventsData";

/*
 * Header mega menus ("Events", "For designers"), modelled on x.ai's nav: hover
 * opens a panel with a list on the left and a preview on the right. A soft
 * highlight slides between list items and the preview crossfades to match.
 */

type NavMenuProps = {
  label: string;
  links: NavLink[];
  /** The current page is one of this menu's links. */
  current?: boolean;
  triggerClassName: string;
};

const OPEN_DELAY = 60;
const CLOSE_DELAY = 140;

const ApplyPreview = () => (
  <div className="w-[248px] rounded-xl border border-gray-200 bg-white p-3.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
    <div className="flex items-center gap-2.5">
      <Image
        src="/home/candidates/chloe-martin.webp"
        alt=""
        width={40}
        height={40}
        className="h-10 w-10 rounded-full object-cover"
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">Your profile</p>
        <p className="text-xs text-gray-500">Product Designer, Paris</p>
      </div>
    </div>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {["Fintech", "Design systems", "Research"].map((tag) => (
        <span
          key={tag}
          className="rounded border border-gray-200 bg-gray-50 px-1.5 text-[11px] leading-5 text-gray-600"
        >
          {tag}
        </span>
      ))}
    </div>
    <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-2 text-[11px] font-medium leading-6 text-gray-700">
      <span className="h-1.5 w-1.5 rounded-full bg-wcd-positive" />
      Matched to hiring teams
    </p>
  </div>
);

const ReviewPreview = () => (
  <div className="w-[248px] rounded-xl border border-gray-200 bg-white p-3.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
    <div className="flex items-baseline justify-between">
      <p className="text-xs font-medium text-gray-500">Hiring manager review</p>
      <span className="rounded bg-wcd-accent-tint px-1.5 text-[10px] font-semibold leading-5 tracking-wide text-wcd-accent-deep">
        PROFESSIONAL
      </span>
    </div>
    <p className="mt-1 tabular-nums">
      <span className="text-lg font-semibold text-wcd-heading">2.4</span>
      <span className="text-xs text-gray-500">/3.0</span>
    </p>
    <ul className="mt-2 space-y-1.5">
      {[
        ["Craft", 2.6],
        ["Research", 2.0],
        ["Systems", 2.5],
        ["Storytelling", 2.3],
      ].map(([label, score]) => (
        <li key={label} className="flex items-center gap-2 text-[11px]">
          <span className="w-16 shrink-0 text-gray-600">{label}</span>
          <span className="h-1 flex-1 overflow-hidden rounded-full bg-[#e5e7eb]">
            <span
              className="block h-full rounded-full bg-[#f97316]"
              style={{ width: `${(Number(score) / 3) * 100}%` }}
            />
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const SalaryPreview = () => (
  <div className="w-[248px] rounded-xl border border-gray-200 bg-white p-3.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
    <p className="text-xs font-medium text-gray-500">
      Product designer salaries by city
    </p>
    <div className="mt-3 flex h-24 items-end gap-2">
      {[62, 88, 74, 55, 96, 68].map((h, i) => (
        <span
          key={i}
          className="flex-1 rounded-t"
          style={{
            height: `${h}%`,
            backgroundColor: i === 4 ? "#f97316" : "#FED7AA",
          }}
        />
      ))}
    </div>
    <div className="mt-1.5 flex gap-2 text-[9px] text-gray-400">
      {["LIS", "BER", "PAR", "WAW", "LON", "AMS"].map((c) => (
        <span key={c} className="flex-1 text-center">
          {c}
        </span>
      ))}
    </div>
  </div>
);

/** Event format previews: the format's own photo or cover. */
/*
 * Workshops preview: code turns into design. A tiny editor types a Button and
 * the button below takes shape as each line lands (colour, radius, label).
 * Ticks only while its menu item is active; reduced motion shows the result.
 */
const CODE = [
  "<Button",
  '  tone="orange"',
  '  radius="full"',
  ">",
  "  Book a call",
  "</Button>",
];
const CODE_LENGTH = CODE.reduce((n, line) => n + line.length, 0);
const TICK_MS = 45;
const HOLD_TICKS = 45;

const lineEnd = (index: number) =>
  CODE.slice(0, index + 1).reduce((n, line) => n + line.length, 0);

const WorkshopPreview: React.FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [typed, setTyped] = useState(CODE_LENGTH);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const host = ref.current?.closest("[data-active]");
    let n = 0;
    let timer = 0;
    const tick = () => {
      n = n >= CODE_LENGTH + HOLD_TICKS ? 0 : n + 1;
      setTyped(Math.min(n, CODE_LENGTH));
    };
    // Type only while this is the active preview of an open menu; idle otherwise.
    const sync = () => {
      window.clearInterval(timer);
      n = 0;
      setTyped(CODE_LENGTH);
      if (host?.getAttribute("data-active") === "true")
        timer = window.setInterval(tick, TICK_MS);
    };
    const observer = new MutationObserver(sync);
    if (host)
      observer.observe(host, {
        attributes: true,
        attributeFilter: ["data-active"],
      });
    sync();
    return () => {
      observer.disconnect();
      window.clearInterval(timer);
    };
  }, []);

  const done = (line: number) => typed >= lineEnd(line);
  let offset = 0;

  return (
    <div
      ref={ref}
      className="w-[248px] overflow-hidden rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.12)]"
    >
      <div className="bg-[#161112] px-3 pb-3 pt-2.5 font-mono text-[11px] leading-[17px]">
        <div className="mb-2 flex items-center gap-1.5">
          {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
            <span
              key={c}
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: c }}
            />
          ))}
          <span className="ml-1.5 text-[10px] text-white/40">Button.tsx</span>
        </div>
        {CODE.map((line, i) => {
          const shown = line.slice(0, Math.max(0, typed - offset));
          const caret =
            typed >= offset &&
            typed < offset + line.length + (i === CODE.length - 1 ? 1 : 0);
          offset += line.length;
          const [, attr, value] = shown.match(/^(\s*\w+=)?(.*)$/) ?? [];
          return (
            <div key={i} className="h-[17px] whitespace-pre">
              {shown.trimStart().startsWith("<") ||
              shown.trimStart().startsWith(">") ? (
                <span className="text-[#F97316]">{shown}</span>
              ) : attr ? (
                <>
                  <span className="text-white/60">{attr}</span>
                  <span className="text-[#9BD06B]">{value}</span>
                </>
              ) : (
                <span className="text-white/90">{shown}</span>
              )}
              {caret && (
                <span className="ml-px inline-block h-3 w-[6px] translate-y-[2px] animate-pulse bg-white/80" />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex h-[64px] items-center justify-center bg-white [background-image:radial-gradient(#E7E1DD_1px,transparent_1px)] [background-size:10px_10px]">
        <span
          className={`flex h-9 items-center justify-center px-5 text-xs font-medium transition-all duration-300 ease-out ${
            done(1)
              ? "bg-[#F97316] text-white"
              : "border border-dashed border-gray-300 bg-white text-transparent"
          } ${done(2) ? "rounded-full" : "rounded-md"}`}
          style={{ minWidth: 110 }}
        >
          <span
            className={`transition-opacity duration-200 ${done(4) ? "opacity-100" : "opacity-0"}`}
          >
            Book a call
          </span>
        </span>
      </div>
    </div>
  );
};

/** Extra photos stacked behind a format's main photo in its menu preview. */
const stackPhotos: Record<string, string[]> = {
  dinners: [
    "/events/05-22-design-leaders-dinner-r1-3.webp",
    "/events/09-29-design-leaders-dinner-r2-6.webp",
  ],
  meetups: [
    "/events/03-19-state-of-design-europe-2.webp",
    "/events/06-04-business-impact-portfolio-8.webp",
  ],
};

/* Back cards rest tucked behind the front photo and fan out while the item is
   hovered (the wrapper sets data-active). */
const fan = [
  "-rotate-2 group-data-[active=true]:-translate-x-9 group-data-[active=true]:-rotate-6",
  "rotate-2 group-data-[active=true]:translate-x-9 group-data-[active=true]:rotate-6",
];

/** Event format previews: the format's photo (stacked when there are more), or its cover. */
const FormatPreview: React.FC<{ id: string }> = ({ id }) => {
  const media = formats.find((f) => f.id === id)?.media;
  if (!media) return null;
  if (media.kind === "cover")
    return (
      <Image
        src={media.src}
        alt=""
        width={media.width}
        height={media.height}
        sizes="200px"
        quality={85}
        className="h-auto max-h-[180px] w-auto rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.12)]"
      />
    );
  // A single photo (the podcast cover) shows larger and uncropped so its text stays legible.
  if (!stackPhotos[id])
    return (
      <div className="relative aspect-video w-[300px] overflow-hidden rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.12)]">
        <Image
          src={media.src}
          alt=""
          fill
          sizes="300px"
          quality={85}
          className="object-cover"
        />
      </div>
    );
  const card =
    "absolute inset-0 overflow-hidden rounded-xl border-[3px] border-white shadow-[0_10px_30px_rgba(0,0,0,0.14)]";
  return (
    <div className="relative h-[144px] w-[210px]">
      {(stackPhotos[id] ?? []).map((src, i) => (
        <div
          key={src}
          className={`${card} transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)] motion-reduce:transition-none ${fan[i]}`}
        >
          <Image
            src={src}
            alt=""
            fill
            sizes="228px"
            quality={85}
            className="object-cover"
          />
        </div>
      ))}
      <div
        className={`${card} transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)] group-data-[active=true]:-translate-y-1 motion-reduce:transition-none`}
      >
        <Image
          src={media.src}
          alt=""
          fill
          sizes="228px"
          quality={85}
          className="object-cover"
        />
      </div>
    </div>
  );
};

/** Preview shown beside each menu item, keyed by link href. */
const previews: Record<string, React.ReactNode> = {
  "/join": <ApplyPreview />,
  "/portfolio-review": <ReviewPreview />,
  "https://data.world-class.design/?mode=salaries": <SalaryPreview />,
  "/events#format-podcast": <FormatPreview id="podcast" />,
  "/events#format-dinners": <FormatPreview id="dinners" />,
  "/events#format-breakfasts": <FormatPreview id="breakfasts" />,
  "/events#format-workshops": <WorkshopPreview />,
  "/events#format-meetups": <FormatPreview id="meetups" />,
};

const NavMenu: React.FC<NavMenuProps> = ({
  label,
  links,
  current,
  triggerClassName,
}) => {
  const items = links;
  const panelId = useId();
  const [open, setOpen] = useState(false);
  // Previews mount after the first open, then stay mounted so reopening is instant.
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    if (open) setOpened(true);
  }, [open]);
  const [active, setActive] = useState(0);
  const timer = useRef<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [highlight, setHighlight] = useState<{
    top: number;
    height: number;
  } | null>(null);

  const schedule = useCallback((next: boolean, delay: number) => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(next), delay);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  // Move the highlight under the active item.
  useEffect(() => {
    const el = itemRefs.current[active];
    if (el) setHighlight({ top: el.offsetTop, height: el.offsetHeight });
  }, [active, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div
      className="relative"
      onPointerEnter={() => schedule(true, OPEN_DELAY)}
      onPointerLeave={() => schedule(false, CLOSE_DELAY)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          schedule(false, 0);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        data-current={current || undefined}
        onClick={() => schedule(!open, 0)}
        className={`${triggerClassName} inline-flex items-center gap-1`}
      >
        {label}
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Invisible bridge so the pointer can travel from the trigger to the panel. */}
      <div className="absolute inset-x-0 top-full h-4" aria-hidden="true" />

      <div
        id={panelId}
        className={`absolute left-1/2 top-[calc(100%+12px)] z-50 w-[640px] -translate-x-1/2 origin-top rounded-2xl border border-black/[0.06] bg-white p-2 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)] transition-[opacity,transform,visibility] duration-200 ease-out motion-reduce:transition-none ${
          open
            ? "visible translate-y-0 scale-100 opacity-100"
            : "invisible -translate-y-1 scale-[0.98] opacity-0"
        }`}
      >
        <div className="flex gap-2">
          <ul className="relative w-[280px] shrink-0 py-1">
            {highlight && (
              <li
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 rounded-xl bg-[#F4F2F0] transition-[top,height] duration-200 ease-out motion-reduce:transition-none"
                style={{ top: highlight.top, height: highlight.height }}
              />
            )}
            {items.map((item, i) => {
              const content = (
                <>
                  <span className="flex items-center gap-1 text-[15px] font-medium text-wcd-heading">
                    {item.title}
                    {item.external && (
                      <ArrowUpRight
                        className="h-3.5 w-3.5 text-wcd-muted"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-wcd-muted">
                    {item.description}
                  </span>
                </>
              );
              const shared = {
                ref: (el: HTMLAnchorElement | null) => {
                  itemRefs.current[i] = el;
                },
                onPointerEnter: () => setActive(i),
                onFocus: () => setActive(i),
                tabIndex: open ? 0 : -1,
                className:
                  "relative block rounded-xl px-4 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#F97316]",
              };
              return (
                <li key={item.title}>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...shared}
                    >
                      {content}
                    </a>
                  ) : (
                    <Link href={item.href} {...shared}>
                      {content}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          <div
            aria-hidden="true"
            className="relative flex-1 overflow-hidden rounded-xl bg-wcd-hero"
          >
            {items.map((item, i) => (
              <div
                key={item.title}
                data-active={i === active && open}
                className={`group absolute inset-0 flex items-center justify-center transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
                  i === active
                    ? "translate-y-0 opacity-100"
                    : "translate-y-2 opacity-0"
                }`}
              >
                {opened && previews[item.href]}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const DesignersMenu: React.FC<Omit<NavMenuProps, "label" | "links">> = (
  props,
) => <NavMenu label="For designers" links={designerLinks} {...props} />;

export const EventsMenu: React.FC<Omit<NavMenuProps, "label" | "links">> = (
  props,
) => <NavMenu label="Events" links={eventLinks} {...props} />;
